/**
 * Hook to check Cylindo 3D viewer and AR availability
 * Based on Cylindo Content API: https://pdp.cylindo.com/API/content-api
 */

import {useEffect, useState} from 'react';

/**
 * Check if Cylindo has a 3D visualization for a product
 * @param {Object} product - Product object
 * @param {Array} imageGroups - Pre-processed image groups from ProductView
 * @param {string} imageSource - Image source ('cylindo' or 'pim')
 * @returns {Object} { has3D, hasAR, cylindoParams, isLoading, error }
 */
export const useCylindoAvailability = (product, imageGroups = [], imageSource = 'cylindo') => {
  const [availability, setAvailability] = useState({
    has3D: false,
    hasAR: false,
    cylindoParams: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!product || imageSource !== 'cylindo' || !imageGroups || imageGroups.length === 0) {
      setAvailability({
        has3D: false,
        hasAR: false,
        cylindoParams: null,
        isLoading: false,
        error: null,
      });

      return;
    }

    // Extract Cylindo parameters from pre-processed image groups
    const params = extractCylindoParams(product, imageGroups);

    if (!params) {
      setAvailability({
        has3D: false,
        hasAR: false,
        cylindoParams: null,
        isLoading: false,
        error: 'Not a Cylindo product',
      });

      return;
    }

    const checkAvailability = async () => {
      try {
        // Check if product has 3D configuration
        const configUrl = `https://content.cylindo.com/api/v2/${params.customerId}/products/${encodeURIComponent(params.productCode)}/configuration`;
        const response = await fetch(configUrl);

        if (response.ok) {
          await response.json();

          // Build ARQR URL for AR functionality
          const arqrUrl = buildARQRUrl(params, product);

          // If configuration exists, product has 3D visualization
          setAvailability({
            has3D: true,
            hasAR: true, // If 3D exists, AR is also available
            cylindoParams: {
              ...params,
              arqrUrl,
            },
            isLoading: false,
            error: null,
          });
        } else if (response.status === 404) {
          // No 3D visualization, but might have AR-only
          const arqrUrl = buildARQRUrl(params, product);

          setAvailability({
            has3D: false,
            hasAR: true, // Still provide AR if possible
            cylindoParams: {
              ...params,
              arqrUrl,
            },
            isLoading: false,
            error: null,
          });
        } else {
          throw new Error(`Configuration check failed: ${response.status}`);
        }
      } catch (error) {
        setAvailability({
          has3D: false,
          hasAR: false,
          cylindoParams: null,
          isLoading: false,
          error: error.message,
        });
      }
    };

    checkAvailability();
  }, [product, imageGroups, imageSource]);

  return availability;
};

/**
 * Build ARQR URL for AR functionality
 * According to Cylindo API: https://pdp.cylindo.com/API/content-api#arqr
 * Format: https://content.cylindo.com/api/v2/{customer_id}/products/{product_code}/ARQR/{filename}?feature={feature}:{option}&redirect_url={url}
 * 
 * @param {Object} params - Cylindo parameters
 * @param {Object} product - Product object
 * @returns {string} ARQR URL
 */
const buildARQRUrl = (params, product) => {
  const {customerId, productCode, configuration} = params;

  // Base ARQR URL with correct format from Cylindo documentation
  // Using product code as filename for the QR code image
  const filename = encodeURIComponent(productCode);
  let arqrUrl = `https://content.cylindo.com/api/v2/${customerId}/products/${encodeURIComponent(productCode)}/ARQR/${filename}`;

  const queryParams = [];

  // Add configuration (features) if available
  // Configuration should be in format: "FEATURE:OPTION" or "FEATURE1:OPTION1, FEATURE2:OPTION2"
  if (configuration) {
    // Split configuration by comma if it contains multiple features
    const features = configuration.split(',').map(f => f.trim());
    features.forEach(feature => {
      queryParams.push(`feature=${encodeURIComponent(feature)}`);
    });
  }

  // Add redirect URL to return user to product page after AR experience
  if (typeof window !== 'undefined') {
    const currentUrl = window.location.href;
    queryParams.push(`redirect_url=${encodeURIComponent(currentUrl)}`);
  }

  // Combine query parameters
  if (queryParams.length > 0) {
    arqrUrl += `?${queryParams.join('&')}`;
  }

  return arqrUrl;
};

/**
 * Extract Cylindo parameters from product images
 * @param {Object} product - Product object
 * @param {Array} imageGroups - Pre-processed image groups
 * @returns {Object} { customerId, productCode, configuration }
 */
export const extractCylindoParams = (product, imageGroups = []) => {
  if (!imageGroups || imageGroups.length === 0) {
    return null;
  }

  // Find the first Cylindo image group (not just the first image group)
  const cylindoImageGroup = imageGroups.find(group => 
    group.imageSource === 'cylindo' && 
    group.images && 
    group.images.length > 0
  );

  if (!cylindoImageGroup) {
    return null;
  }

  // Get first image URL from the Cylindo group
  const firstImage = cylindoImageGroup.images[0];

  if (!firstImage?.link) {
    return null;
  }

  try {
    const url = new URL(firstImage.link);

    // Check if it's a Cylindo URL
    if (!url.hostname.includes('cylindo.com')) {
      return null;
    }

    // Parse URL path: /api/v2/{customerId}/products/{productCode}/...
    const pathParts = url.pathname.split('/').filter(Boolean);

    if (pathParts.length < 5 || pathParts[0] !== 'api' || pathParts[1] !== 'v2') {
      return null;
    }

    const customerId = pathParts[2];
    const productCode = decodeURIComponent(pathParts[4]);

    // Extract configuration from URL parameters
    const features = url.searchParams.getAll('feature');
    const configuration = features.join(', ') || undefined;

    return {
      customerId,
      productCode,
      configuration,
    };
  } catch (error) {
    return null;
  }
};
